import { useState, useEffect } from "react";

import { fetchMachineTypes } from "./machineTypes";

export async function getConstants() {
  const machineTypes = await fetchMachineTypes();

  // Calculate maximum available resources across all machine types
  const maxResources = machineTypes.reduce(
    (max, spec) => ({
      cpus: Math.max(max.cpus, spec.cpus),
      memory: Math.max(max.memory, spec.memory),
      gpu: Math.max(max.gpu, spec.gpu),
      gpuMemory: Math.max(max.gpuMemory, spec.gpuMemory),
    }),
    {
      cpus: 0,
      memory: 0,
      gpu: 0,
      gpuMemory: 0,
    }
  );

  // Find max charge for CPU machine
  const maxCpuCharge = machineTypes
    .filter(spec => spec.gpu === 0)
    .reduce((max, spec) => Math.max(max, spec.chargePerHour), 0);

  // Find GPU machine price
  const gpuMachine = machineTypes.find(spec => spec.gpu > 0);
  if (!gpuMachine) {
    throw new Error("No GPU machine type found");
  }

  return {
    MAX_CPU: maxResources.cpus,
    MAX_MEMORY: maxResources.memory,
    MAX_GPU: maxResources.gpu,
    BASE_PRICE: maxCpuCharge,
    GPU_PRICE: gpuMachine.chargePerHour,
  };
}

// Create a hook to use the constants
export function useConstants() {
  const [constants, setConstants] = useState<Awaited<ReturnType<typeof getConstants>> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function loadConstants() {
      try {
        const result = await getConstants();
        setConstants(result);
        setIsLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err : new Error("Failed to load constants"));
        setIsLoading(false);
      }
    }
    loadConstants();
  }, []);

  return { constants, isLoading, error };
}
